import { createFileRoute } from '@tanstack/react-router';
import useMapsStore from '@/store/maps';
import dynamic from 'next/dynamic';
import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import apiConfig from '@/config/url-api';
import useAuthStore from '@/store/auth';
import { useQueryUser } from '@/api/queries/auth';
import Loader from '@/components/ui/loader';

const MapsContainer = dynamic(() => import('@/featured/apps/components/maps/MapsContainer'), {
    ssr: false,
});

const MapsLayer = dynamic(() => import('@/featured/apps/components/maps/maps-layer'), {
    ssr: false,
});

const MapsVessels = dynamic(() => import('@/featured/apps/components/maps/MapsVessels'), {
    ssr: false,
});

const MapsMarker = dynamic(() => import('@/featured/apps/components/maps/maps-marker'), {
    ssr: false,
});

const MapsToolbarSettings = dynamic(
    () => import('@/featured/apps/components/maps/maps-toolbar-settings'),
    {
        ssr: false,
    },
);

const MapsConfig = dynamic(() => import('@/featured/apps/components/maps/MapsConfig'), {
    ssr: false,
});

export default function AppsEmbed() {
    const useGlobalMaps = useMapsStore((state) => state);
    const { maps } = useGlobalMaps;
    const router = useRouter();
    const authStore = useAuthStore((state) => state);
    
    const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
    const { isError, data, refetch, isFetching } = useQueryUser();
    const initializedRef = useRef(false);

    useEffect(() => {
        if (!router.isReady) return;
        if (initializedRef.current) return;
        initializedRef.current = true;

        let code: string | null = null;
        let provider: string | null = null;
        let ais: string | null = null;
        let windy: string | null = null;

        if (typeof window !== 'undefined') {
            const searchParams = new URLSearchParams(window.location.search);
            code = searchParams.get('code') || (router.query as any)?.code || null;
            provider = searchParams.get('provider') || (router.query as any)?.provider || null;
            ais = searchParams.get('ais') || (router.query as any)?.ais || null;
            windy = searchParams.get('windy') || (router.query as any)?.windy || null;
        }

        if (windy === '1' || windy === 'true') {
            useMapsStore.getState().setOverlaysSettings('windyMap', true);
        }

        if (provider === 'mst' || provider === 'stream' || provider === 'mt' || ais === 'true' || ais === '1') {
            useMapsStore.getState().setOverlaysSettings('marineTraffic', true);
        }

        if (code && typeof window !== 'undefined') {
            window.localStorage.setItem(apiConfig.auth.storageTokenKeyName, String(code));
            
            // Clean code from URL query without stripping other params and without router.replace infinite loop
            const url = new URL(window.location.href);
            if (url.searchParams.has('code')) {
                url.searchParams.delete('code');
                window.history.replaceState(window.history.state, '', url.pathname + (url.search ? url.search : '') + url.hash);
            }
        }

        const storedToken = typeof window !== 'undefined' ? window.localStorage.getItem(apiConfig.auth.storageTokenKeyName) : null;

        if (!storedToken && authStore.user === null) {
            setIsLoggedIn(false);
        } else if (authStore.user === null) {
            refetch();
        } else {
            setIsLoggedIn(true);
        }
    }, [router.isReady]);

    useEffect(() => {
        if (data) {
            if (data.settings && typeof data.settings === 'string' && !data.settingsObj) {
                try {
                    data.settingsObj = JSON.parse(data.settings);
                } catch {}
            }
            authStore.setLogin(data);
            setIsLoggedIn(true);
        } else if (isError) {
            setIsLoggedIn(false);
            if (typeof window !== 'undefined') {
                window.localStorage.removeItem(apiConfig.auth.storageTokenKeyName);
                window.localStorage.removeItem(apiConfig.auth.storageUserKeyName);
            }
        }
    }, [data, isError]);

    if (isLoggedIn === false) {
        return (
            <div className="flex h-screen w-screen items-center justify-center bg-gray-50 p-4">
                <div className="w-full max-w-sm rounded-xl bg-white p-6 text-center shadow-lg">
                    <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                        <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h2 className="mb-2 text-xl font-bold text-gray-900">Akses Ditolak</h2>
                    <p className="text-sm text-gray-500">Silakan login terlebih dahulu melalui aplikasi untuk mengakses peta.</p>
                </div>
            </div>
        );
    }

    if (isLoggedIn === null || (isFetching && authStore.user === null)) {
        return <Loader />;
    }

    return (
        <>
            <div className="flex">
                <section className="relative h-screen w-screen">
                    <MapsContainer className="relative h-full w-full">
                        {maps && (
                            <>
                                <MapsVessels/>
                                <MapsLayer/>
                                <MapsToolbarSettings/>
                                <MapsConfig/>
                            </>
                        )}
                    </MapsContainer>

                    {maps && <MapsMarker maps={maps}/>}
                </section>
            </div>
        </>
    );
}

export const Route = createFileRoute('/_authenticated/apps-embed')({
  component: AppsEmbed,
});
